import React, { useState, useEffect, useRef } from 'react';
import { CheckCheck, Trash2, Filter, RefreshCw, X, Loader2 } from 'lucide-react';
import { Notification, NotificationQuery } from '@/types';
import notificationService from '@/services/notificationService';
import NotificationItem from './NotificationItem';

interface NotificationDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  onNotificationClick?: (notification: Notification) => void;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  isOpen,
  onClose,
  onNotificationClick,
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  // Fetch notifications
  const fetchNotifications = async (reset = false) => {
    if (isLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      const query: NotificationQuery = {
        page: reset ? 1 : page,
        limit: 20,
        isRead: filter === 'unread' ? false : undefined,
      };

      const response = await notificationService.getNotifications(query);

      if (response.success && response.data) {
        const newNotifications = response.data.notifications;

        if (reset) {
          setNotifications(newNotifications);
          setPage(2);
        } else {
          setNotifications(prev => [...prev, ...newNotifications]);
          setPage(prev => prev + 1);
        }

        setHasMore(response.data.pagination.page < response.data.pagination.pages);
      } else {
        setError(response.message || 'Failed to fetch notifications');
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setError('Failed to fetch notifications');
    } finally {
      setIsLoading(false);
    }
  };

  // Load notifications when dropdown opens or filter changes
  useEffect(() => {
    if (isOpen) {
      fetchNotifications(true);
    }
  }, [isOpen, filter]);

  // Infinite scroll
  const handleScroll = () => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer || isLoading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      fetchNotifications();
    }
  };

  // Mark notification as read
  const handleMarkAsRead = async (id: string) => {
    try {
      const response = await notificationService.markAsRead(id);
      if (response.success) {
        setNotifications(prev =>
          prev.map(notif => (notif._id === id ? { ...notif, isRead: true } : notif))
        );
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Delete notification
  const handleDelete = async (id: string) => {
    try {
      const response = await notificationService.deleteNotification(id);
      if (response.success) {
        setNotifications(prev => prev.filter(notif => notif._id !== id));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      const response = await notificationService.markAllAsRead();
      if (response.success) {
        setNotifications(prev => prev.map(notif => ({ ...notif, isRead: true })));
      }
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  // Clear all notifications
  const handleClearAll = async () => {
    if (!window.confirm('Are you sure you want to delete all notifications?')) {
      return;
    }

    try {
      const response = await notificationService.deleteAllNotifications();
      if (response.success) {
        setNotifications([]);
      }
    } catch (error) {
      console.error('Failed to clear all notifications:', error);
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification: Notification) => {
    if (onNotificationClick) {
      onNotificationClick(notification);
    }

    // Mark as read if not already read
    if (!notification.isRead) {
      handleMarkAsRead(notification._id);
    }
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      className="absolute right-0 top-full mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-[500px] flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
          {unreadCount > 0 && (
            <span className="bg-brand-100 text-brand-800 text-xs font-medium px-2 py-1 rounded-full">
              {unreadCount} new
            </span>
          )}
        </div>

        <div className="flex items-center space-x-1">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
            title="Filter options"
          >
            <Filter className="w-4 h-4" />
          </button>

          <button
            onClick={() => fetchNotifications(true)}
            disabled={isLoading}
            className="p-1 text-gray-400 hover:text-gray-600 rounded disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>

          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-4">
            <label className="flex items-center space-x-2">
              <input
                type="radio"
                value="all"
                checked={filter === 'all'}
                onChange={e => setFilter(e.target.value as 'all' | 'unread')}
                className="text-brand-primary"
              />
              <span className="text-sm text-gray-700">All</span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="radio"
                value="unread"
                checked={filter === 'unread'}
                onChange={e => setFilter(e.target.value as 'all' | 'unread')}
                className="text-brand-primary"
              />
              <span className="text-sm text-gray-700">Unread only</span>
            </label>
          </div>
        </div>
      )}

      {/* Actions */}
      {notifications.length > 0 && (
        <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
          <button
            onClick={handleMarkAllAsRead}
            className="text-sm text-brand-primary hover:text-brand-800 font-medium flex items-center space-x-1"
            disabled={unreadCount === 0}
          >
            <CheckCheck className="w-4 h-4" />
            <span>Mark all read</span>
          </button>

          <button
            onClick={handleClearAll}
            className="text-sm text-red-600 hover:text-red-800 font-medium flex items-center space-x-1"
          >
            <Trash2 className="w-4 h-4" />
            <span>Clear all</span>
          </button>
        </div>
      )}

      {/* Content */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto" onScroll={handleScroll}>
        {error && (
          <div className="p-4 text-center text-red-600 bg-red-50">
            <p className="text-sm">{error}</p>
            <button
              onClick={() => fetchNotifications(true)}
              className="mt-2 text-xs text-red-700 underline"
            >
              Try again
            </button>
          </div>
        )}

        {notifications.length === 0 && !isLoading && !error && (
          <div className="p-8 text-center text-gray-500">
            <div className="text-4xl mb-2">🔔</div>
            <p className="text-sm">No notifications yet</p>
            <p className="text-xs text-gray-400 mt-1">
              {filter === 'unread' ? 'No unread notifications' : "You're all caught up!"}
            </p>
          </div>
        )}

        <div className="p-3 space-y-2">
          {notifications.map(notification => (
            <NotificationItem
              key={notification._id}
              notification={notification}
              onMarkRead={handleMarkAsRead}
              onDelete={handleDelete}
              onClick={handleNotificationClick}
            />
          ))}
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            <span className="ml-2 text-sm text-gray-500">Loading...</span>
          </div>
        )}

        {!hasMore && notifications.length > 0 && (
          <div className="text-center py-4 text-sm text-gray-400">No more notifications</div>
        )}
      </div>
    </div>
  );
};

export default NotificationDropdown;
